import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Modal, Button, Input, Tree, Empty, Spin } from "antd";
const { Search } = Input;
/**
 * 通用树形视图模态框组件
 * 参考 GenericModalForm 的设计模式，提供高度可配置的树形展示功能
 */
const GenericModalTree = ({ visible, title, size = {}, onClose, treeData = [], searchConfig = {}, selectionConfig = {}, actionConfig = {}, treeConfig = {}, modalProps = {}, emptyText = "暂无数据", loadingText = "加载中...", }) => {
    // 默认尺寸配置
    const defaultSize = {
        width: 600,
        height: "60vh",
        maxWidth: "80vw",
        maxHeight: "80vh",
        minWidth: 400,
        minHeight: "40vh",
    };
    // 合并尺寸配置
    const modalSize = { ...defaultSize, ...size };
    // 默认配置合并
    const { enabled: searchEnabled = true, placeholder: searchPlaceholder = "搜索...", onSearch: onSearchCallback, } = searchConfig || {};
    const { enabled: selectionEnabled = false, mode: selectionMode = "multiple", checkable = false, selectedKeys: externalSelectedKeys = [], checkedKeys: externalCheckedKeys = [], onSelectionChange, onCheckChange, } = selectionConfig || {};
    const { showConfirm = true, showCancel = true, showClose = false, confirmText = "确认", cancelText = "取消", closeText = "关闭", onConfirm, onCancel, } = actionConfig || {};
    const { defaultExpandAll = true, showLine = { showLeafIcon: false }, showIcon = true, loading = false, } = treeConfig || {};
    // 内部状态
    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState(defaultExpandAll ? getAllKeys(treeData) : []);
    const [internalSelectedKeys, setInternalSelectedKeys] = useState(externalSelectedKeys);
    const [internalCheckedKeys, setInternalCheckedKeys] = useState(externalCheckedKeys);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    // 获取所有节点的key（用于默认展开）
    function getAllKeys(nodes) {
        const keys = [];
        const traverse = (nodeList) => {
            nodeList.forEach((node) => {
                keys.push(node.key);
                if (node.children) {
                    traverse(node.children);
                }
            });
        };
        traverse(nodes);
        return keys;
    }
    // 同步外部状态
    useEffect(() => {
        if (selectionEnabled) {
            setInternalSelectedKeys(externalSelectedKeys);
        }
        if (checkable) {
            setInternalCheckedKeys(externalCheckedKeys);
        }
    }, [externalSelectedKeys, externalCheckedKeys, selectionEnabled, checkable]);
    // 重置状态
    useEffect(() => {
        if (visible) {
            setSearchValue("");
            // 根据defaultExpandAll设置展开状态
            if (defaultExpandAll) {
                setExpandedKeys(getAllKeys(treeData));
            }
            else {
                setExpandedKeys([]);
            }
            setAutoExpandParent(true);
        }
    }, [visible, defaultExpandAll, treeData]);
    // 处理搜索
    const handleSearch = (value) => {
        setSearchValue(value);
        if (onSearchCallback) {
            onSearchCallback(value);
        }
        // 自动展开匹配的节点
        if (value) {
            const matchedKeys = new Set();
            const parentKeysToExpand = new Set();
            const getMatchedKeys = (nodes, parent) => {
                nodes.forEach((node) => {
                    const nodeWithParent = { ...node, parent };
                    const titleText = typeof node.title === "string"
                        ? node.title
                        : node.title?.toString() || "";
                    if (titleText.toLowerCase().includes(value.toLowerCase())) {
                        matchedKeys.add(node.key);
                        // 添加父节点到展开列表
                        let currentParent = parent;
                        while (currentParent) {
                            parentKeysToExpand.add(currentParent.key);
                            currentParent = currentParent.parent;
                        }
                    }
                    if (node.children) {
                        getMatchedKeys(node.children, nodeWithParent);
                    }
                });
            };
            getMatchedKeys(treeData);
            setExpandedKeys(Array.from(parentKeysToExpand));
            setAutoExpandParent(true);
        }
        else {
            setExpandedKeys([]);
            setAutoExpandParent(false);
        }
    };
    // 处理节点选择
    const handleSelect = (selectedKeys, info) => {
        if (selectionEnabled && onSelectionChange) {
            const keys = selectionMode === "single" ? selectedKeys.slice(-1) : selectedKeys;
            setInternalSelectedKeys(keys);
            onSelectionChange(keys);
        }
    };
    // 处理复选框变化
    const handleCheck = (checkedKeys, info) => {
        if (checkable && onCheckChange) {
            const keys = Array.isArray(checkedKeys)
                ? checkedKeys
                : checkedKeys.checked || [];
            setInternalCheckedKeys(keys);
            onCheckChange(keys);
        }
    };
    // 处理确认
    const handleConfirm = () => {
        if (onConfirm) {
            const keysToConfirm = checkable
                ? internalCheckedKeys
                : internalSelectedKeys;
            onConfirm(keysToConfirm);
        }
        handleClose();
    };
    // 处理取消
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
        handleClose();
    };
    // 处理关闭
    const handleClose = () => {
        // 重置选择状态
        setInternalCheckedKeys([]);
        setInternalSelectedKeys([]);
        setExpandedKeys([]);
        setSearchValue("");
        onClose();
    };
    // 过滤树形数据
    const filteredTreeData = useMemo(() => {
        if (!searchValue)
            return treeData;
        const filterTree = (nodes) => {
            return nodes.reduce((acc, node) => {
                const titleText = typeof node.title === "string"
                    ? node.title
                    : node.title?.toString() || "";
                const matches = titleText
                    .toLowerCase()
                    .includes(searchValue.toLowerCase());
                const children = node.children ? filterTree(node.children) : [];
                if (matches || children.length > 0) {
                    acc.push({
                        ...node,
                        children: children.length > 0 ? children : node.children,
                    });
                }
                return acc;
            }, []);
        };
        return filterTree(treeData);
    }, [treeData, searchValue]);
    // 渲染操作按钮
    const renderFooter = () => {
        const buttons = [];
        if (showCancel) {
            buttons.push(_jsx(Button, { onClick: handleCancel, children: cancelText }, "cancel"));
        }
        if (showConfirm && selectionEnabled) {
            buttons.push(_jsx(Button, { type: "primary", onClick: handleConfirm, children: confirmText }, "confirm"));
        }
        if (showClose && !selectionEnabled) {
            buttons.push(_jsx(Button, { onClick: onClose, children: closeText }, "close"));
        }
        return buttons.length > 0 ? buttons : null;
    };
    // 渲染内容
    const renderContent = () => {
        if (loading) {
            return (_jsxs("div", { style: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                    gap: 16,
                }, children: [_jsx(Spin, { size: "large" }), _jsx("span", { style: { color: "#666" }, children: loadingText })] }));
        }
        if (!treeData || treeData.length === 0) {
            return (_jsx("div", { style: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                }, children: _jsx(Empty, { description: emptyText }) }));
        }
        return (_jsx(Tree, { treeData: filteredTreeData, checkable: checkable, selectable: selectionEnabled, selectedKeys: selectionEnabled ? internalSelectedKeys : undefined, checkedKeys: checkable ? internalCheckedKeys : undefined, onSelect: selectionEnabled ? handleSelect : undefined, onCheck: checkable ? handleCheck : undefined, expandedKeys: expandedKeys, onExpand: (keys) => {
                setExpandedKeys(keys);
                setAutoExpandParent(false);
            }, autoExpandParent: autoExpandParent, defaultExpandAll: defaultExpandAll, showLine: showLine, showIcon: showIcon, titleRender: (nodeData) => nodeData.title, filterTreeNode: (node) => {
                if (!searchValue)
                    return true;
                const titleText = typeof node.title === "string"
                    ? node.title
                    : node.title?.toString() || "";
                return titleText.toLowerCase().includes(searchValue.toLowerCase());
            } }));
    };
    return (_jsx(Modal, { title: title, open: visible, width: modalSize.width, onCancel: handleClose, footer: renderFooter(), style: {
            top: 20,
            maxWidth: modalSize.maxWidth,
            minWidth: modalSize.minWidth,
        }, styles: {
            body: {
                height: modalSize.height,
                maxHeight: modalSize.maxHeight,
                minHeight: modalSize.minHeight,
                padding: 0,
                display: "flex",
                flexDirection: "column",
            },
        }, ...modalProps, children: _jsxs("div", { style: {
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }, children: [searchEnabled && !loading && treeData.length > 0 && (_jsx("div", { style: {
                        padding: "16px 16px 0 16px",
                        flexShrink: 0,
                    }, children: _jsx(Search, { placeholder: searchPlaceholder, value: searchValue, onChange: (e) => handleSearch(e.target.value), allowClear: true, enterButton: true }) })), _jsxs("div", { style: {
                        flex: 1,
                        overflow: "hidden",
                        padding: treeData.length > 0 ? "16px" : "0",
                        display: "flex",
                        flexDirection: "column",
                    }, children: [treeData.length > 0 && (_jsx("div", { style: {
                                flex: 1,
                                overflow: "auto",
                                border: COMPONENT_OUTLINE_BORDER,
                                borderRadius: "6px",
                                padding: "16px",
                            }, children: renderContent() })), treeData.length === 0 && renderContent()] })] }) }));
};
export default GenericModalTree;
